import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Package, TrendingUp, AlertTriangle,
  ArrowUpRight, Lightbulb, DollarSign, RefreshCw, Zap
} from 'lucide-react';
import { keyed } from '../lib/listKeys';

const URGENCY_CONFIG = {
  critical: { color: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30', icon: AlertTriangle },
  high: { color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30', icon: ArrowUpRight },
  medium: { color: 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30', icon: TrendingUp },
  low: { color: 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30', icon: Package },
};

function QuickFireDialog({ open, recommendations, onClose }) {
  const [selected, setSelected] = useState(new Set());
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      // Auto-select all critical/high items
      const autoSelect = (recommendations || []).filter(r => r.urgency === 'critical' || r.urgency === 'high').map(r => r.product_id || r.product_name);
      setSelected(new Set(autoSelect));
      api.get('/suppliers', { params: { status: 'active', limit: 100 } }).then(r => setSuppliers(r.data.suppliers || [])).catch(() => {});
    }
  }, [open, recommendations]);

  const toggleItem = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectedItems = (recommendations || []).filter(r => selected.has(r.product_id || r.product_name));

  const create = async () => {
    if (!selectedItems.length) { toast.error('Select at least one item'); return; }
    if (!supplierName.trim()) { toast.error('Supplier name is required'); return; }
    setCreating(true);
    try {
      const res = await api.post('/purchase-orders/quick-fire', {
        supplier_id: supplierId || null,
        supplier_name: supplierName.trim(),
        items: selectedItems.map(r => ({
          product_id: r.product_id || '',
          product_name: r.product_name,
          quantity_ordered: r.recommended_order || 1,
          unit_cost: 0,
        })),
        notes: `Auto-generated from AI Reorder Quick-Fire — ${selectedItems.length} items`,
      });
      toast.success(`PO ${res.data.po_id} created with ${selectedItems.length} items`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create PO');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> Quick-Fire PO</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Select items and a supplier to create a draft PO instantly.</p>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Supplier</Label>
            <Select value={supplierId} onValueChange={id => { setSupplierId(id); const sup = suppliers.find(s => s.supplier_id === id); if (sup) setSupplierName(sup.name); }}>
              <SelectTrigger data-testid="qf-supplier-select"><SelectValue placeholder="Choose supplier…" /></SelectTrigger>
              <SelectContent>{suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Or type supplier name</Label>
            <Input value={supplierName} onChange={e => setSupplierName(e.target.value)} placeholder="Supplier name" data-testid="qf-supplier-name" />
          </div>
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-2">
          {(recommendations || []).map((rec, i) => {
            const id = rec.product_id || rec.product_name;
            const cfg = URGENCY_CONFIG[rec.urgency] || URGENCY_CONFIG.medium;
            return (
              <div key={i} className="flex items-center gap-2 p-2 rounded hover:bg-muted/50">
                <Checkbox checked={selected.has(id)} onCheckedChange={() => toggleItem(id)} id={`qf-${i}`} data-testid={`qf-item-${i}`} />
                <label htmlFor={`qf-${i}`} className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{rec.product_name}</span>
                    <Badge variant="outline" className={`text-xs ${cfg.color}`}>{rec.urgency}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">Stock: {rec.current_stock} · Suggest: <strong>{rec.recommended_order}</strong> units</div>
                </label>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">{selected.size} of {(recommendations || []).length} items selected</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={creating || !selected.size || !supplierName.trim()} data-testid="qf-create-po-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}Create Draft PO
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ReorderPage() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showQuickFire, setShowQuickFire] = useState(false);

  const fetchRecommendations = async () => {
    setLoading(true);
    setData(null);
    try {
      const res = await api.post('/ai/reorder-recommendations');
      setData(res.data);
      toast.success('AI recommendations generated');
    } catch (err) {
      const { handleAIError } = await import('../lib/aiErrorHandler');
      handleAIError(err, 'Failed to get recommendations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="reorder-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            AI Reorder Recommendations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Smart inventory analysis powered by Gemini AI
          </p>
        </div>
        <div className="flex gap-2">
          {data?.recommendations?.length > 0 && (
            <Button variant="outline" onClick={() => setShowQuickFire(true)} className="gap-2 h-10 text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="quick-fire-btn">
              <Zap className="h-4 w-4" /> Quick-Fire PO
            </Button>
          )}
          <Button onClick={fetchRecommendations} disabled={loading} className="gap-2 h-10" data-testid="generate-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {data ? 'Refresh' : 'Generate'} Recommendations
          </Button>
        </div>
      </div>

      {!data && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Sparkles className="h-12 w-12 mx-auto mb-4 text-primary opacity-50" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              AI-Powered Inventory Intelligence
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
              Analyzes your current stock levels, recent sales trends, and demand patterns to suggest optimal reorder quantities.
            </p>
            <Button onClick={fetchRecommendations} className="gap-2" data-testid="initial-generate-btn">
              <Sparkles className="h-4 w-4" /> Generate Recommendations
            </Button>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card className="animate-pulse" data-testid="loading-card">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-3" />
            <p className="text-sm font-medium">AI is analyzing inventory and demand patterns...</p>
            <p className="text-xs text-muted-foreground mt-1">This may take a few seconds</p>
          </CardContent>
        </Card>
      )}

      {data && !loading && (
        <div className="space-y-6 animate-fade-in-up" data-testid="recommendations-results">
          {/* Summary */}
          {data.estimated_savings && (
            <Card className="border-primary/20 bg-primary/5" data-testid="savings-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <DollarSign className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Estimated Impact</p>
                  <p className="text-xs text-muted-foreground">{data.estimated_savings}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {data.recommendations?.length > 0 && (
            <Card data-testid="recommendations-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-primary" /> Reorder Recommendations ({data.recommendations.length})
                </CardTitle>
                <CardDescription>Prioritized by urgency level</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-3">
                    {keyed(data.recommendations, rec => rec.product_id || rec.product_name).map(({ item: rec, key }, i) => {
                      const cfg = URGENCY_CONFIG[rec.urgency] || URGENCY_CONFIG.medium;
                      return (
                        <div key={key} className="p-4 bg-muted/50 rounded-lg space-y-2" data-testid={`recommendation-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <cfg.icon className="h-4 w-4 shrink-0" />
                              <span className="text-sm font-semibold">{rec.product_name}</span>
                            </div>
                            <Badge variant="outline" className={`text-xs ${cfg.color}`}>
                              {rec.urgency}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-4 text-xs">
                            <span className="text-muted-foreground">
                              Current Stock: <strong className="text-foreground">{rec.current_stock}</strong>
                            </span>
                            <span className="text-primary font-semibold">
                              Recommended Order: {rec.recommended_order} units
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{rec.reason}</p>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Insights */}
          {data.insights?.length > 0 && (
            <Card data-testid="insights-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" /> AI Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {keyed(data.insights).map(({ item: insight, key }, i) => (
                    <li key={key} className="flex items-start gap-2 text-sm" data-testid={`insight-${i}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-muted-foreground text-center">
            Recommendations based on current inventory levels and 7-day sales trends. Review before placing orders.
          </p>
        </div>
      )}

      <QuickFireDialog
        open={showQuickFire}
        recommendations={data?.recommendations}
        onClose={() => setShowQuickFire(false)}
      />
    </div>
  );
}
